import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LuCheck, LuChartPie, LuChevronLeft, LuChevronRight, LuFileUp, LuLandmark, LuLogOut,
  LuSlidersHorizontal, LuTag, LuTarget,
} from 'react-icons/lu';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import useAiUsage from '../../hooks/useAiUsage';
import { aiUsageMark } from '../../utils/aiUsage';
import AiUsageRing from '../settings/AiUsageRing';
import { fetchAccountFreshness } from '../../api/endpointsBackend';
import useSheetHistory from '../../hooks/useSheetHistory';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import PhoneConfirmSheet from './PhoneConfirmSheet';
import OwnerAvatar from '../shared/OwnerAvatar';

// A statement status that means the account's statements are not current.
const LATE_STATUSES = ['behind', 'outdated'];

// On a short screen (320×568, a phone in landscape) the rows and the gaps
// between groups tighten, so the sheet still leaves the page showing above.
const SHORT_SCREEN = '@media screen and (max-height: 600px)';

/** One group of rows on a card, as iOS settings lists draw them. */
function RowGroup({ children, mt = '12px', dividerInset = '54px' }) {
  return (
    <Box
      mx="16px"
      mt={mt}
      borderRadius="14px"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      overflow="hidden"
      sx={{
        [SHORT_SCREEN]: { mt: mt === '12px' ? '8px' : mt },
        '& > * + *': { position: 'relative' },
        '& > * + *::before': {
          content: '""', position: 'absolute', top: 0, left: dividerInset, right: 0,
          height: '1px', bg: 'redesign.border',
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * A 56px row (48px on a short screen): icon, label, and a trailing value or
 * chevron. `leading` puts a node (a workspace owner's photo) where the icon
 * goes, in the same column, so the dividers still start at the text.
 */
function Row({
  icon, leading, label, trailing, chevron = true, onClick, color,
}) {
  return (
    <Box
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      display="grid"
      gridTemplateColumns="28px minmax(0, 1fr) auto"
      gap="12px"
      alignItems="center"
      w="100%"
      minH="56px"
      px="14px"
      textAlign="left"
      color={color || 'redesign.heading'}
      _active={onClick ? { bg: 'redesign.bg.hover' } : undefined}
      sx={{ [SHORT_SCREEN]: { minH: '48px' } }}
    >
      {leading || <Box as={icon} boxSize="20px" color={color || 'phone.muted'} />}
      <Text textStyle="phoneBody" noOfLines={1}>{label}</Text>
      <Flex align="center" gap="6px" color="phone.muted" fontSize="15px" whiteSpace="nowrap">
        {trailing}
        {chevron && onClick && <Box as={LuChevronRight} boxSize="18px" />}
      </Flex>
    </Box>
  );
}

const ownerFirstName = (ws) => {
  if (ws.owner_full_name) return ws.owner_full_name.split(' ')[0];
  if (ws.owner_email) return ws.owner_email.split('@')[0];
  return '';
};

/** The same name the desktop workspace switcher shows. */
function useWorkspaceName() {
  const { t } = useTranslation('workspaces');
  return (ws) => {
    if (ws.workspace_role === 'Owner') return t('switcher.ownTitle');
    const owner = ownerFirstName(ws);
    return owner ? t('switcher.sharedTitle', { owner }) : ws.name;
  };
}

function RoleBadge({ role }) {
  const { t } = useTranslation('workspaces');
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      h="22px"
      px="8px"
      borderRadius="11px"
      bg="phone.chipBg"
      color="redesign.text"
      fontSize="12px"
      fontWeight="600"
      flexShrink={0}
    >
      {t(`roles.${role}`, role)}
    </Box>
  );
}

function MainList({ onShowWorkspaces, onOpenSettings, onLogout, behind }) {
  const { t } = useTranslation(['navigation', 'workspaces', 'common']);
  const { close } = usePhoneSheet();
  const navigate = useNavigate();
  const { workspaces, current, can } = useWorkspace();
  const goalsEnabled = useGoalsEnabled();
  const workspaceName = useWorkspaceName();
  const several = workspaces.length > 1;
  // Close to an AI budget, or past it: the ring the More tab carries, beside
  // Settings, whose list shows the Plan bars.
  const aiMark = aiUsageMark(useAiUsage().usage);
  // A second tap while the sheet is already leaving does nothing.
  const leaving = useRef(false);

  // Leave the sheet's history entry before going anywhere, or the pending
  // back step would undo the navigation. `fromMore` gives the page its
  // "‹ More" button.
  const go = (path) => async () => {
    if (leaving.current) return;
    leaving.current = true;
    await close();
    navigate(path, { state: { fromMore: true } });
  };

  const rows = [
    { key: 'accounts', icon: LuLandmark, label: t('accounts'), onClick: go('/accounts') },
    can('source_file.read') && {
      key: 'statements',
      icon: LuFileUp,
      label: t('statements'),
      onClick: go('/source-files'),
      trailing: behind > 0 && (
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          h="22px"
          px="8px"
          borderRadius="11px"
          bg="phone.alertBg"
          color="phone.alert"
          fontSize="12px"
          fontWeight="600"
        >
          {t(behind === 1 ? 'more.behindOne' : 'more.behindOther', { count: behind })}
        </Box>
      ),
    },
    { key: 'tags', icon: LuTag, label: t('tags'), onClick: go('/tags') },
    { key: 'categories', icon: LuChartPie, label: t('categories'), onClick: go('/categories') },
    goalsEnabled && can('goal.read') && {
      key: 'goals', icon: LuTarget, label: t('goals'), onClick: go('/goals'),
    },
    {
      key: 'settings',
      icon: LuSlidersHorizontal,
      label: t('settings'),
      trailing: aiMark && <AiUsageRing mark={aiMark} size="18px" />,
      onClick: async () => {
        if (leaving.current) return;
        leaving.current = true;
        await close();
        onOpenSettings?.();
      },
    },
  ].filter(Boolean);

  return (
    <>
      {current && (
        <RowGroup mt="6px">
          <Row
            leading={<OwnerAvatar workspace={current} size={28} />}
            label={workspaceName(current)}
            trailing={t(`workspaces:roles.${current.workspace_role}`, current.workspace_role)}
            onClick={several ? onShowWorkspaces : undefined}
          />
        </RowGroup>
      )}
      <RowGroup>
        {rows.map(({ key, ...row }) => <Row key={key} {...row} />)}
      </RowGroup>
      <RowGroup>
        <Row icon={LuLogOut} label={t('common:actions.logout')} color="phone.expense" chevron={false} onClick={onLogout} />
      </RowGroup>
      <Text textStyle="phoneMeta" color="phone.muted" px="32px" pt="14px">
        {t('more.footer')}
      </Text>
    </>
  );
}

function WorkspaceList() {
  const { t } = useTranslation('navigation');
  const { close } = usePhoneSheet();
  const { workspaces, currentId, switchWorkspace } = useWorkspace();
  const workspaceName = useWorkspaceName();

  return (
    <>
      {/* Each space leads with its owner's photo, as the desktop switcher's
          rows do: yours on your space, the host's on a shared one. */}
      <RowGroup mt="6px">
        {workspaces.map((ws) => (
          <Row
            key={ws.id}
            leading={<OwnerAvatar workspace={ws} size={28} />}
            label={(
              <Flex as="span" align="center" gap="8px" minW={0}>
                {/* A Box, not a Text: Text's own size would override the row's. */}
                <Box as="span" noOfLines={1}>{workspaceName(ws)}</Box>
                <RoleBadge role={ws.workspace_role} />
              </Flex>
            )}
            trailing={ws.id === currentId && <Box as={LuCheck} boxSize="22px" color="phone.link" />}
            chevron={false}
            // Switching reloads the app, so the sheet's entry goes first.
            onClick={async () => { await close(); switchWorkspace(ws.id); }}
          />
        ))}
      </RowGroup>
      <Text textStyle="phoneMeta" color="phone.muted" px="32px" pt="14px">
        {t('more.membersNote')}
      </Text>
    </>
  );
}

/**
 * Everything that is not a daily job: the workspace, the read-only pages,
 * Settings and Log out. With more than one workspace, the workspace row
 * pushes the list of them inside the sheet; the back swipe returns from it.
 */
export default function MoreSheet({ isOpen, onClose, onOpenSettings }) {
  const { t } = useTranslation('navigation');
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { can } = useWorkspace();
  const canReadFiles = can('source_file.read');
  const [view, setView] = useState('main');
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [behind, setBehind] = useState(0);
  // The workspace list keeps the sheet at the main list's height, so pushing
  // it reads as a page turning inside the same sheet.
  const bodyRef = useRef(null);
  const mainSheetHeight = useRef(0);
  const [listMinH, setListMinH] = useState(undefined);

  useEffect(() => {
    if (!isOpen) setView('main');
  }, [isOpen]);

  const showWorkspaces = () => {
    mainSheetHeight.current = bodyRef.current?.closest('.chakra-modal__content')?.offsetHeight || 0;
    setView('workspaces');
  };

  useLayoutEffect(() => {
    if (view !== 'workspaces') {
      setListMinH(undefined);
      return;
    }
    const body = bodyRef.current;
    const sheet = body?.closest('.chakra-modal__content');
    if (!sheet || !body || !mainSheetHeight.current) return;
    const short = mainSheetHeight.current - sheet.offsetHeight;
    if (short > 0) setListMinH(`${body.offsetHeight + short}px`);
  }, [view]);

  // The same freshness the desktop's Statement status card reads.
  useEffect(() => {
    if (!isOpen || !canReadFiles) return undefined;
    let active = true;
    fetchAccountFreshness()
      .then((data) => {
        const late = (data?.accounts || []).filter((a) => LATE_STATUSES.includes(a.status));
        if (active) setBehind(late.length);
      })
      .catch(() => { /* the pill is a hint; without it the row still works */ });
    return () => { active = false; };
  }, [isOpen, canReadFiles]);

  const workspaces = useSheetHistory('more-workspaces', isOpen && view === 'workspaces', {
    onPop: () => setView('main'),
  });

  return (
    <PhoneSheet
      id="more"
      isOpen={isOpen}
      onClose={onClose}
      size="content"
      title={view === 'workspaces' ? t('more.workspaces') : undefined}
      headerLeft={view === 'workspaces' ? (
        <IconButton
          icon={<LuChevronLeft size={24} />}
          variant="ghost"
          borderRadius="full"
          color="redesign.text"
          minW="44px"
          minH="44px"
          aria-label={t('more.back')}
          onClick={() => workspaces.close()}
        />
      ) : undefined}
    >
      <Box ref={bodyRef} minH={view === 'workspaces' ? listMinH : undefined}>
        {view === 'workspaces' ? (
          <WorkspaceList />
        ) : (
          <MainList
            behind={behind}
            onShowWorkspaces={showWorkspaces}
            onOpenSettings={onOpenSettings}
            onLogout={() => setLogoutOpen(true)}
          />
        )}
      </Box>
      <LogoutConfirm
        isOpen={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onLogout={() => { logout(); navigate('/login'); }}
      />
    </PhoneSheet>
  );
}

/** Log out asks first; on the desktop it is immediate. */
function LogoutConfirm({ isOpen, onCancel, onLogout }) {
  const { t } = useTranslation(['navigation', 'common']);
  const { close } = usePhoneSheet();
  return (
    <PhoneConfirmSheet
      isOpen={isOpen}
      title={t('more.logoutTitle')}
      confirmLabel={t('common:actions.logout')}
      destructive
      onConfirm={async () => {
        onCancel();
        await close();
        onLogout();
      }}
      onCancel={onCancel}
    />
  );
}
